import { CANCELED_BY_USER } from "@modules/common/types/errorTypes"
import { Config } from "@modules/config"
import { ConfigService } from "@modules/core/services/configService"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { Path } from "@modules/navigation/routes"
import { getMySpacePath } from "@modules/navigation/useMySpaceNavigate"
import { CheckoutContainer } from "@modules/payment/components/checkoutContainer"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { ProgramBundleDetailsTabs } from "@modules/programBundle/components/programBundleInfoResume"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useToast } from "@modules/ui/components/huToast"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoggerType } from "@modules/utils/loggerUtils"
import { loadStripe } from "@stripe/stripe-js"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useTheme } from "styled-components"

export const useProgramBundleSubscription = (programBundleId: string) => {
  const { programBundleSubscriberApi } = useServicesContext()
  const queryClient = useQueryClient()
  const toast = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ["programBundleSubscription", programBundleId],
    queryFn: async () => {
      const result = await programBundleSubscriberApi.getProgramBundleSubscription(programBundleId)
      return result ? ProgramBundleSubscriberEntity.fromItemDto(result) : null
    },
  })

  const { mutateAsync: followProgamsInProgramBundle, isPending: isFollowProgamsInProgramBundle } = useMutation({
    mutationFn: async (programIds: string[]) => {
      await programBundleSubscriberApi.followProgramBundle(programBundleId, programIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["programBundles", programBundleId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programBundleSubscription", programBundleId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programBundlesSubscribed"],
      })
      queryClient.invalidateQueries({
        queryKey: ["trainingSessions"],
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "programOnOffProgression",
      })

      toast.show({ severity: "success", detail: word("programBundle.follow.add.success") })
    },
    onError: (e) => {
      toast.show({ severity: "error", summary: word("programBundle.follow.add.error"), detail: e.message })
    },
  })

  const { mutateAsync: unFollowProgamsInProgramBundle, isPending: isUnFollowProgamsInProgramBundle } = useMutation({
    mutationFn: async (programIds: string[]) => {
      await programBundleSubscriberApi.unfollowProgramBundle(programBundleId, programIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["programBundles", programBundleId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programBundleSubscription", programBundleId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programBundlesSubscribed"],
      })
      queryClient.invalidateQueries({
        queryKey: ["trainingSessions"],
      })
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "programOnOffProgression",
      })

      toast.show({ severity: "success", detail: word("programBundle.follow.remove.success") })
    },
    onError: (e) => {
      toast.show({ severity: "error", summary: word("programBundle.follow.remove.error"), detail: e.message })
    },
  })

  const isPending = isFollowProgamsInProgramBundle || isUnFollowProgamsInProgramBundle

  return {
    data,
    isLoading,
    followProgamsInProgramBundle,
    unFollowProgamsInProgramBundle,
    isPending,
  }
}

export const useProgramBundleAthleteSubscription = (programBundle: ProgramBundleEntity) => {
  const [isUpdating, setIsUpdating] = useState(false)

  const {
    programService,
    userFinanceApi,
    programBundleSubscriberApi,
    planningService,
    programInfiniteService,
    programOnOffService,
  } = useServicesContext()

  const confirmPopup = useConfirmPopup()
  const toast = useToast()
  const theme = useTheme()
  const { sidebarNavigateTo, setCurrentStackState } = useSidebar()
  const queryClient = useQueryClient()
  const returnUrl = `${ConfigService.config.WEB_URL}${getMySpacePath(Path.MySpace.Training)}`

  const { isAuthor } = useProgramBundleRights(programBundle)
  const isOnOffProgramBundle = programBundle.type === ProgramBundleType.ON_OFF

  const { data: programBundleStripeSubscriptionInfos, isLoading } = useProgramBundleSubscription(programBundle.id)

  const subscribe = async (): Promise<boolean> => {
    const isInPendingPayment =
      programBundleStripeSubscriptionInfos &&
      programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT
    const isInactive =
      (programBundleStripeSubscriptionInfos &&
        programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE) ||
      !programBundleStripeSubscriptionInfos
    const canSubscribeToProgramBundle = isInactive || isInPendingPayment

    if (!programBundle || !canSubscribeToProgramBundle) {
      return false
    }
    if (!programBundle.price) {
      return await joinProgramBundle()
    }
    return await joinProgramBundleWithPayment()
  }

  const joinProgramBundle = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)

      const { subscriptionStatus } = await programBundleSubscriberApi.subscribeProgramBundle(programBundle.id)

      await queryClient.invalidateQueries({
        queryKey: ["programBundleSubscription", programBundle.id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["programBundle", programBundle.id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["programBundlesSubscribed"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["trainingSessions"],
      })
      const store =
        programBundle.type === ProgramBundleType.ON_OFF
          ? programOnOffService.programOnOffStore
          : programInfiniteService.programInfiniteStore

      await store.items.get().forEach(async (program) => await store.fetch(program.id)) // Refetch already fetched programs
      await planningService.refreshSessions() // Refetch sessions of last opened program

      if (programBundle.programs) planningService.addFilteredPrograms(programBundle.programs.map((p) => p.id)) // Add programs to filtered programs on training page

      if (subscriptionStatus === ProgramSubscriptionStatus.PENDING_REQUEST) {
        toast.show({ severity: "success", detail: word("program.subscription.success.toast") })
      }
      if (subscriptionStatus === ProgramSubscriptionStatus.ACTIVE) {
        toast.show({
          severity: "success",
          detail: word(isOnOffProgramBundle ? "program.onOff.buy.success.toast" : "program.subscribe.success.toast"),
        })

        setTimeout(() => {
          setCurrentStackState("activeIndex", ProgramBundleDetailsTabs.PROGRAMS)
        }, 600)
      }
      return true
    } catch (e: any) {
      if (e.code === "PAID_PROGRAM_SUBSCRIPTION") {
        console.warn(LoggerType.Program + "try to free subscribe on paid program, refresh program bundle` ...")
        queryClient.invalidateQueries({
          queryKey: ["programBundle", programBundle.id],
        })
      } else {
        console.error(LoggerType.Program + "subscribe", e)
        toast.show({
          severity: "error",
          detail: word(isOnOffProgramBundle ? "program.onOff.buy.error.toast" : "program.subscribe.error.toast"),
        })
      }
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const joinProgramBundleWithPayment = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)
      const result = await programService.subscribeToPayingProgram(programBundle.id, returnUrl, true)

      const stripe = await loadStripe(Config.STRIPE_PUBLIC_KEY, { stripeAccount: result.coachStripeAccountId })

      if (stripe) {
        sidebarNavigateTo({
          content: (
            <CheckoutContainer clientSecret={result.clientSecret} stripePromise={stripe} program={programBundle} />
          ),
          leftButtonColor: theme.mainBlack,
        })
      }

      return Promise.resolve(true)
    } catch (e: any) {
      if (e !== CANCELED_BY_USER) {
        if (e.code === "PROGRAM_HAS_NO_PRICE") {
          console.warn(LoggerType.Program + "try to paid a free program, refresh program ...")
          queryClient.invalidateQueries({
            queryKey: ["programBundle", programBundle.id],
          })
        } else {
          await queryClient.invalidateQueries({
            queryKey: ["programBundleSubscription", programBundle.id],
          })
          console.error(LoggerType.Stripe + "subscribe on paid program", e)
          toast.show({
            detail: word(isOnOffProgramBundle ? "program.onOff.buy.error.toast" : "program.subscribe.error.toast"),
            severity: "error",
          })
        }
      }
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const tryNewPayment = async (): Promise<boolean> => {
    if (programBundleStripeSubscriptionInfos?.subscriptionId != null) {
      try {
        setIsUpdating(true)
        const url = await userFinanceApi.getPaymentAuthenticationUrl(
          returnUrl,
          undefined,
          programBundleStripeSubscriptionInfos.programBundleSubscriptionId,
        )

        window.open(url, "_blank", "noreferrer")
        return Promise.resolve(true)
      } catch (e) {
        console.error(LoggerType.Program + "get authentication url failed", e)
        toast.show({ detail: word("program.payment.authentication.error.toast"), severity: "error" })
        return false
      } finally {
        setIsUpdating(false)
      }
    }
    return false
  }

  const openPaymentSettings = async (): Promise<boolean> => {
    try {
      setIsUpdating(true)
      const result = await programService.getProgramPaymentSubscription(programBundle.id, returnUrl, true)

      window.open(result.url, "_blank", "noreferrer")

      return Promise.resolve(true)
    } catch (e: any) {
      console.error(LoggerType.Program + "get payment settings url failed", e)
      toast.show({ detail: word("program.payment.authentication.error.toast"), severity: "error" })
      return false
    } finally {
      setIsUpdating(false)
    }
  }

  const unsubscribe = async (): Promise<void> => {
    const isActive =
      programBundleStripeSubscriptionInfos &&
      programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE
    const canUnsubscribeToProgramBundle =
      isActive ||
      (programBundleStripeSubscriptionInfos &&
        (programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.PENDING_PAYMENT ||
          programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.TRIALING))

    if (programBundle && !isAuthor && canUnsubscribeToProgramBundle) {
      try {
        const processUnsubscribe = async () => {
          setIsUpdating(true)
          await programBundleSubscriberApi.unsubscribeProgramBundle(programBundle.id)
          await programService.paginatedSubscribedprogramStore.list()

          await queryClient.invalidateQueries({
            queryKey: ["programBundleSubscription", programBundle.id],
          })
          await queryClient.invalidateQueries({
            queryKey: ["programBundle", programBundle.id],
          })
          await queryClient.invalidateQueries({
            queryKey: ["programBundlesSubscribed"],
          })
          await queryClient.invalidateQueries({
            queryKey: ["trainingSessions"],
          })
          const store =
            programBundle.type === ProgramBundleType.ON_OFF
              ? programOnOffService.programOnOffStore
              : programInfiniteService.programInfiniteStore
          await store.items.get().forEach(async (program) => await store.fetch(program.id)) // Refetch already fetched programs

          await planningService.refreshSessions() // Refetch sessions of last opened program

          setIsUpdating(false)
        }

        if (
          programBundle.price &&
          isActive &&
          programBundleStripeSubscriptionInfos &&
          "endPeriodDate" in programBundleStripeSubscriptionInfos &&
          typeof programBundleStripeSubscriptionInfos.endPeriodDate === "string"
        ) {
          confirmPopup.show({
            title: word("program.subscription.paying.unsubscribe.confirm.title"),
            message: word("program.subscription.paying.unsubscribe.confirm.description", {
              endPeriodDate:
                (programBundleStripeSubscriptionInfos.endPeriodDate &&
                  getFormatedDateTime(programBundleStripeSubscriptionInfos.endPeriodDate)) ??
                word("global.missingInformation"),
            }),
            accept: async () => {
              await processUnsubscribe()
            },
            acceptValidated: word("program.unsubscribe.success.toast"),
            footerProps: {
              align: true,
            },
          })
        } else if (!isActive) {
          confirmPopup.show({
            title: word(
              programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.TRIALING
                ? "program.subscription.trialing.cancel.popup.title"
                : "program.details.pendingPayment.popup.title",
            ),
            message: word(
              programBundleStripeSubscriptionInfos.subscriptionStatus === ProgramSubscriptionStatus.TRIALING
                ? "program.subscription.trialing.cancel.popup.description"
                : "program.details.pendingPayment.popup.description",
            ),
            accept: async () => {
              await processUnsubscribe()
            },
            acceptValidated: word("program.unsubscribe.success.toast"),
            footerProps: {
              align: true,
            },
          })
        } else {
          await processUnsubscribe()
        }
      } catch (e) {
        console.error(LoggerType.Program + "unsubscribe", e)
        toast.show({ detail: word("program.unsubscribe.error.toast"), severity: "error" })
      } finally {
        setIsUpdating(false)
      }
    }
  }

  return {
    isUpdating,
    isLoading,
    subscribe,
    unsubscribe,
    joinProgramBundleWithPayment,
    programBundleStripeSubscriptionInfos,
    tryNewPayment,
    openPaymentSettings,
  }
}
